import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useContext, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  ButtonToolbar,
  Col,
  Form,
  Row,
  Table,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import type { AccountsController } from "../../../../api/src/accounts/accounts.controller";
import type { Account } from "../../../../api/src/accounts/accounts.service";
import type { InsightsController } from "../../../../api/src/insights/insights.controller";
import type { UnpackResponse } from "../../../../api/src/lib";
import ActionButton from "../../components/ActionButton";
import IndeterminateCheckbox from "../../components/IndeterminateCheckbox";
import Loading from "../../Loading";
import {
  useAuthenticatedMutationNew,
  useAuthenticatedQuery,
} from "../../lib/api";
import { displayAccountName } from "../../lib/display";
import {
  resolveAccountTaxType,
  type TaxRegistration,
} from "../../lib/tax-registration";
import { NotificationContext } from "../../Notifications";
import { useQueryAccounts } from "./lib";

const TAX_REG_OPTIONS = [
  { value: "", label: "— Select tax classification —" },
  { value: "taxable", label: "Taxable (Brokerage)" },
  { value: "tax_deferred", label: "Tax-Deferred (Trad IRA, 401k, SEP)" },
  { value: "tax_free", label: "Tax-Free (Roth IRA, Roth 401k, HSA)" },
  { value: "inherited_deferred", label: "Inherited IRA (Traditional)" },
  { value: "inherited_free", label: "Inherited Roth IRA" },
] as const;

function useQueryUnknownTaxClassificationAccountIds() {
  return useAuthenticatedQuery<
    UnpackResponse<InsightsController["unknownTaxClassification"]>,
    number[],
    [string, string]
  >({
    queryKey: ["insights", "unknown-tax-classification"],
    queryFn: async (response) => response.data,
  });
}

function useBulkAssignTaxRegistration() {
  return useAuthenticatedMutationNew<
    UnpackResponse<AccountsController["bulkAssignTaxRegistration"]>,
    { accountId: number; taxRegistration: TaxRegistration | null }[],
    { accountIds: number[]; taxRegistration: TaxRegistration | null }
  >({
    // `PUT /v1/accounts/tax-registration`, which was
    // `PUT /accounts/tax-registration/assign` (APP-1653). The key's first path
    // element stays `"accounts"`, so `useQueryAllAccounts` still refreshes on
    // it; the url is under `/v1`.
    mutationKey: ["PUT", "accounts", "tax-registration"],
    urlBuilderFn: (key) => `/v1/${key.join("/")}`,
    mutationFn: ({ data }) => data,
  });
}

const BulkTaxClassificationRow = ({
  account,
  selected,
  onToggle,
}: {
  account: Account;
  selected: boolean;
  onToggle: () => void;
}) => {
  const { label } = resolveAccountTaxType(account);

  return (
    <tr>
      <td>
        <IndeterminateCheckbox checked={selected} onChange={onToggle} />
      </td>
      <td>
        <Link to={`/clients/accounts/${account.id}`}>
          {displayAccountName(account.displayName, account.displayNumber)}
        </Link>
      </td>
      <td>{account.type}</td>
      <td>{account.owner}</td>
      <td>
        <Badge bg="warning" text="dark">
          {label}
        </Badge>
      </td>
    </tr>
  );
};

const BulkTaxClassification = () => {
  const queryClient = useQueryClient();
  const notificationContext = useContext(NotificationContext);

  const {
    isPending: isPendingIds,
    isError: isErrorIds,
    refetch: refetchIds,
    data: accountIds,
  } = useQueryUnknownTaxClassificationAccountIds();

  const {
    isPending: isPendingAccounts,
    isError: isErrorAccounts,
    refetch: refetchAccounts,
    data: accountsResponse,
  } = useQueryAccounts(
    {
      pageSize: 500,
      enabled: typeof accountIds !== "undefined" && accountIds.length > 0,
    },
    accountIds,
  );

  const accounts = useMemo(
    () => accountsResponse?.data ?? [],
    [accountsResponse],
  );

  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [bulkValue, setBulkValue] = useState<string>("");

  const { mutateAsync, isPending: isSubmitting } =
    useBulkAssignTaxRegistration();

  const selectedIds = useMemo(
    () =>
      Object.entries(selected)
        .filter(([, isSelected]) => isSelected)
        .map(([id]) => parseInt(id, 10)),
    [selected],
  );

  const allSelected =
    accounts.length > 0 && selectedIds.length === accounts.length;
  const someSelected =
    selectedIds.length > 0 && selectedIds.length < accounts.length;

  const onToggleAll = useCallback(() => {
    if (allSelected) {
      setSelected({});
    } else {
      setSelected(
        accounts.reduce<Record<number, boolean>>((acc, account) => {
          acc[account.id] = true;
          return acc;
        }, {}),
      );
    }
  }, [accounts, allSelected]);

  const onToggleRow = useCallback((accountId: number) => {
    setSelected((prev) => ({ ...prev, [accountId]: !prev[accountId] }));
  }, []);

  const onApply = useCallback(async () => {
    if (selectedIds.length === 0 || !bulkValue) return;
    const taxRegistration = bulkValue as TaxRegistration;

    try {
      const updated = await mutateAsync({
        accountIds: selectedIds,
        taxRegistration,
      });
      notificationContext.pushNotification({
        id: "bulk-tax-classification-success",
        header: "Tax Classification Updated",
        body: `${updated.length} account${updated.length === 1 ? "" : "s"} updated`,
        variant: "success",
      });
    } catch (err) {
      console.error("Failed to bulk-update tax registration", err);
      notificationContext.pushNotification({
        id: "bulk-tax-classification-failure",
        header: "Failed to Update Tax Classification",
        body: "No accounts were updated",
        variant: "danger",
      });
      return;
    }

    queryClient.invalidateQueries({
      queryKey: ["insights", "unknown-tax-classification"],
    });
    queryClient.invalidateQueries({ queryKey: ["accounts"] });

    setSelected({});
    setBulkValue("");
  }, [bulkValue, mutateAsync, notificationContext, queryClient, selectedIds]);

  const isError = isErrorIds || isErrorAccounts;
  const onRetry = useCallback(() => {
    if (isErrorIds) {
      refetchIds();
    } else {
      refetchAccounts();
    }
  }, [isErrorIds, refetchIds, refetchAccounts]);

  const isPending = isPendingIds || (!!accountIds?.length && isPendingAccounts);

  return (
    <>
      <Row className="mb-3">
        <Col>
          <h3>Bulk Assign Tax Classification</h3>
          <p className="text-muted">
            These accounts have an unknown tax classification because Bridge did
            not report taxable or tax-deferred and no manual override was set.
            Pick a value, select the accounts it applies to, and click Apply.
          </p>
        </Col>
      </Row>
      <Row>
        <Col>
          <ButtonToolbar className="mb-3 align-items-center">
            <ButtonGroup className="me-3">
              <Form.Select
                value={bulkValue}
                onChange={(ev) => setBulkValue(ev.currentTarget.value)}
                disabled={isSubmitting}
                style={{ minWidth: 320 }}
              >
                {TAX_REG_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
            </ButtonGroup>
            <ButtonGroup>
              <ActionButton
                variant="primary"
                label="Apply"
                icon="/icons/save.svg"
                onClick={onApply}
                disabled={
                  selectedIds.length === 0 || !bulkValue || isSubmitting
                }
              />
            </ButtonGroup>
            <span className="ms-3 text-muted align-self-center">
              {selectedIds.length} of {accounts.length} selected
            </span>
          </ButtonToolbar>
        </Col>
      </Row>
      <Row>
        <Col>
          {isPending ? (
            <Loading />
          ) : isError ? (
            <Alert variant="danger">
              <p>
                The accounts with an unknown tax classification did not load
              </p>
              <Button variant="outline-danger" size="sm" onClick={onRetry}>
                Try again
              </Button>
            </Alert>
          ) : accounts.length === 0 ? (
            <Alert variant="success">
              All accounts have a tax classification. Nothing to do here.
            </Alert>
          ) : (
            <Table hover responsive>
              <thead>
                <tr>
                  <th style={{ width: 40 }}>
                    <IndeterminateCheckbox
                      aria-label="Select all accounts"
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={onToggleAll}
                    />
                  </th>
                  <th>Account</th>
                  <th>Type</th>
                  <th>Owner</th>
                  <th>Current Classification</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((account) => (
                  <BulkTaxClassificationRow
                    key={account.id}
                    account={account}
                    selected={!!selected[account.id]}
                    onToggle={() => onToggleRow(account.id)}
                  />
                ))}
              </tbody>
            </Table>
          )}
        </Col>
      </Row>
    </>
  );
};

export default BulkTaxClassification;
